import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import {Button, Checkbox, Dropdown, Input, Space, Tooltip} from 'antd';
import {DownOutlined, PlusOutlined} from '@ant-design/icons';
import Paragraph from 'antd/es/typography/Paragraph';
import {
  StyledTagSelect,
  StyledTagSelectMenu,
  StyledTagSelectMenuItem,
  StyledTagSelectDropdownContent,
  StyledTagSelectDropdownContainer,
  StyledTagSelectSearchContainer,
  StyledTagSelectTag,
  StyledTagSelectTagsList,
  StyledTagSelectButton,
  StyledTagSelectMeasurementContainer
} from './StyledTagSelect';
import './TagSelect.css';
import {useOnClickOutside} from '../../hooks';
import {objectHelpers} from '../../utils/helpers';

const defaultInputProps = {
  allowClear: true
}

const TagSelect = ({
  defaultSelected = [],
  dropdownProps = {},
  emptyText = 'No results found',
  isEnableAdd = false,
  inputProps,
  options = [],
  onAdd,
  onChange,
  selected = [],
  variant = 'input',
  autoLimit = false, // NEW PROP
  ...rest
}) => {
  const [t] = useTranslation('main');
  const [isOpenDropdown, setIsOpenDropdown] = useState(false);
  const [selectedValues, setSelectedValues] = useState(defaultSelected);
  const [searchedOptions, setSearchedOptions] = useState(options);
  const [searchedValue, setSearchedValue] = useState('');
  const [isUpdated, setIsUpdated] = useState(false);
  const tagsListRef = useRef(null);
  const measureContainerRef = useRef(null);
  // Separate state for measurement and display
  const [pendingMaxVisible, setPendingMaxVisible] = useState(selectedValues.length);
  const [maxVisible, setMaxVisible] = useState(selectedValues.length);

  useEffect(() => {
    if (!objectHelpers.arraysIsEqual(selectedValues, selected)) setSelectedValues(selected);
  }, [selected]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setSearchedOptions(options);
    if (searchedValue) setSearchedValue('');
  }, [options]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isOpenDropdown && isUpdated && onChange) {
      setIsUpdated(false);
      onChange?.(selectedValues);
    }
  }, [isOpenDropdown]); // eslint-disable-line react-hooks/exhaustive-deps

  // useLayoutEffect for immediate measurement and DOM update
  useLayoutEffect(() => {
    if (!autoLimit) return;
    if (!measureContainerRef.current || !tagsListRef.current) return;
    const measureEl = measureContainerRef.current;
    const visibleEl = tagsListRef.current;
    // If all tags fit, update maxVisible and stop
    if (measureEl.scrollWidth <= visibleEl.clientWidth) {
      if (maxVisible !== pendingMaxVisible) setMaxVisible(pendingMaxVisible);
      return;
    }
    // If not, decrement pendingMaxVisible and rerender measurement only
    if (pendingMaxVisible > 1) setPendingMaxVisible(prev => prev - 1);
  }, [pendingMaxVisible, selectedValues, variant, autoLimit]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!autoLimit) {
      setPendingMaxVisible(selectedValues.length);
      setMaxVisible(selectedValues.length);
      return;
    }
    setPendingMaxVisible(selectedValues.length);
    setMaxVisible(selectedValues.length);
  }, [selectedValues, variant, autoLimit]);

  const isButtonVariant = variant === 'button';
  const isSingleSelectVariant = variant === 'single-select';

  const handleOpenDropdown = () => setIsOpenDropdown(!isOpenDropdown);

  const handleMenuClick = ({key, domEvent}) => {
    let values = isSingleSelectVariant ? [key] : selectedValues.includes(key) ? selectedValues.filter(i => i !== key) : [...selectedValues, key];
    setSelectedValues(values);
    setIsUpdated(true);
    domEvent.preventDefault();
    domEvent.stopPropagation();

    if (isSingleSelectVariant) setIsOpenDropdown(false);
  }

  const onClickOutside = (e) => {
    const parentSelector = '.ant-dropdown';
    if (!e.target.closest(parentSelector)) setIsOpenDropdown(false);
  }

  const getTagLabel = (value) => {
    let option = options.find(o => o.value === value);
    return option?.value || value;
  }

  const onSearch = (e) => {
    let {value} = e.target;
    setSearchedValue(value);

    value = value.toLowerCase();
    setSearchedOptions(options.filter(o => o.label.toLowerCase().indexOf(value) >= 0));
  }

  const handleRemoveTag = (value, key) => {
    const values = selectedValues.filter((s, index) => index !== key && s !== value);
    setSelectedValues(values);
    onChange && onChange(values);
  }

  const handleAdd = () => {
    if (searchedValue) {
      const values = [...selectedValues, searchedValue];
      setSelectedValues(values);
      setSearchedValue('');
      setSearchedOptions([...options, {label: searchedValue, value: searchedValue}]);
      onChange && onChange(values);
      onAdd && onAdd(searchedValue);
    }
  }

  const searchedOptionsLength = searchedOptions.length;

  const isEnableAdding = searchedValue !== '' && searchedOptionsLength === 0;

  // Only use maxVisible for the visible tag list
  const renderSelectedValues = () => {
    if (!autoLimit) {
      return selectedValues.map((tag, i) => (
        <StyledTagSelectTag key={i} onClose={() => handleRemoveTag(tag, i)} closable>
          {getTagLabel(tag)}
        </StyledTagSelectTag>
      ));
    }
    const safeMax = selectedValues.length === 0 ? 0 : Math.max(maxVisible, 1);
    const visibleTags = selectedValues.slice(0, safeMax);
    const hiddenTags = selectedValues.slice(safeMax);
    return [
      ...visibleTags.map((tag, i) => (
        <StyledTagSelectTag key={i} onClose={() => handleRemoveTag(tag, i)} closable>
          {getTagLabel(tag)}
        </StyledTagSelectTag>
      )),
      hiddenTags.length > 0 && (
        <Tooltip title={hiddenTags.map(getTagLabel).join(', ')} key='plus'>
          <StyledTagSelectTag bordered={false} style={{backgroundColor: 'transparent'}}>+{hiddenTags.length}</StyledTagSelectTag>
        </Tooltip>
      )
    ].filter(Boolean);
  };

  // Hidden measurement container for tag fitting calculation
  const renderMeasurementTags = () => {
    const tags = [
      ...selectedValues.slice(0, pendingMaxVisible).map((tag, i) => (
        <StyledTagSelectTag closable key={i}>
          {getTagLabel(tag)}
        </StyledTagSelectTag>
      )),
      selectedValues.length > pendingMaxVisible && (
        <StyledTagSelectTag
          bordered={false}
          key='plus'
          style={{backgroundColor: 'transparent'}}
        >
          +{selectedValues.length - pendingMaxVisible}
        </StyledTagSelectTag>
      ),
      isButtonVariant && (
        <StyledTagSelectButton
          key='add'
          icon={<PlusOutlined />}
          size='small'
          type='text'
        />
      )
    ];
    return tags.filter(Boolean);
  };

  const searchComponent = (
    <Input
      allowClear={true}
      size='large'
      placeholder={t('search')}
      onChange={onSearch}
      value={searchedValue}
    />
  );

  return (
    <StyledTagSelect
      variant={variant}
      {...rest}
    >
      {autoLimit && (
        <StyledTagSelectMeasurementContainer ref={measureContainerRef}>
          {renderMeasurementTags()}
        </StyledTagSelectMeasurementContainer>
      )}

      <StyledTagSelectDropdownContainer
        ref={useOnClickOutside(onClickOutside, isOpenDropdown, null, true)}
      >
        <Dropdown
          classNames={{root: 'tag-select-overlay'}}
          popupRender={() => (
            <StyledTagSelectDropdownContent>
              <StyledTagSelectSearchContainer className='search'>
                {isEnableAdd ? (
                  <Space.Compact>
                    {searchComponent}
                    <Button
                      disabled={!isEnableAdding}
                      onClick={() => isEnableAdding && handleAdd()}
                      size='large'
                    >
                      {t('add')}
                    </Button>
                  </Space.Compact>
                ) : searchComponent}
              </StyledTagSelectSearchContainer>
              <div className='dropdown-searched-content'>
                {searchedOptionsLength > 0 && (
                  <StyledTagSelectMenu
                    multiple={!isSingleSelectVariant}
                    onClick={handleMenuClick}
                    selectedKeys={selectedValues}
                  >
                    {searchedOptions.map(item => {
                      const {label, value} = item;
                      return (
                        <StyledTagSelectMenuItem
                          key={value}
                        >
                          {isSingleSelectVariant ? label : (
                            <Checkbox checked={selectedValues.includes(value)}>
                              {label}
                            </Checkbox>
                          )}
                        </StyledTagSelectMenuItem>
                      );
                    })}
                  </StyledTagSelectMenu>
                )}

                {searchedOptionsLength === 0 && searchedOptionsLength !== options.length && (
                  <Paragraph>
                    {emptyText}
                  </Paragraph>
                )}
              </div>
            </StyledTagSelectDropdownContent>
          )}
          open={isOpenDropdown}
          overlayStyle={{minWidth: 200}}
          placement='bottomLeft'
          {...dropdownProps}
        >
          {!isButtonVariant ? (
            <Input
              {...defaultInputProps}
              {...inputProps}
              onClick={handleOpenDropdown}
              readOnly
              suffix={(
                <DownOutlined
                  onClick={handleOpenDropdown}
                  style={{ color: 'rgba(0,0,0,.25)', width: 12, height: 12 }}
                />
              )}
              value={isSingleSelectVariant ? options.find(o => o.value === selectedValues?.[0])?.label || '' : ''}
            />
          ) : <span/>}
        </Dropdown>
      </StyledTagSelectDropdownContainer>

      {((selectedValues.length > 0 || isButtonVariant) && !isSingleSelectVariant) && (
        <StyledTagSelectTagsList ref={tagsListRef} variant={variant}>
          {renderSelectedValues()}

          {isButtonVariant && (
            <StyledTagSelectButton
              icon={<PlusOutlined />}
              onClick={handleOpenDropdown}
              size='small'
              type='text'
            />
          )}
        </StyledTagSelectTagsList>
      )}
    </StyledTagSelect>
  );
};

TagSelect.propTypes = {
  defaultSelected: PropTypes.array,
  emptyText: PropTypes.string,
  inputProps: PropTypes.object,
  isEnableAdd: PropTypes.bool,
  onAdd: PropTypes.func,
  onChange: PropTypes.func,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    })
  ),
  selected: PropTypes.array,
  variant: PropTypes.oneOf(['button', 'input', 'single-select'])
};

export default TagSelect;
